<template>
    <div class="mt-5 grid-cols-layout pb-5 lg:grid">
        <div class="mb-6 lg:mb-0 lg:mt-4">
            <h3
                class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
            >
                Two Factor Authentication
            </h3>
            <p class="max-w-xs text-sm text-gray-500">
                Two-Factor Authentication (2FA) can be used to help protect your
                account from unauthorized access by requiring you to enter a
                security code when you sign in.
            </p>
        </div>
        <div class="mx-auto w-full rounded-md bg-white shadow-card lg:mx-0">
            <div class="border-b px-6 pb-6">
                <p
                    v-if="!twoFactorEnabled"
                    class="text-md pt-6 leading-normal text-gray-800"
                >
                    Two-Factor Authentication is
                    <span class="font-medium text-custom-red-600">{{
                        $t("vue.not_enabled")
                    }}</span>
                </p>
                <p v-else class="text-md pt-6 leading-normal text-gray-800">
                    Two-Factor Authentication is
                    <span class="font-medium text-green-600">{{
                        $t("vue.enabled")
                    }}</span>
                </p>
                <p class="pt-6 text-sm leading-normal text-gray-500">
                    When Two-Factor Authentication (2FA) is enabled, when you
                    sign in you’ll be required to use the security code provided
                    by your Authenticator App of choice.
                </p>

                <ConfirmsPassword
                    v-if="twoFactorEnabled"
                    @confirmed="showRecoveryCodes = true"
                >
                    <AtomicButton class="mt-6" color="white" size="sm">
                        Show recovery codes
                    </AtomicButton>
                </ConfirmsPassword>
                <ConfirmsPassword
                    v-if="twoFactorEnabled"
                    @confirmed="showPairingCodes = true"
                >
                    <AtomicButton class="ml-3 mt-6" color="white" size="sm">
                        Pairing QR code
                    </AtomicButton>
                </ConfirmsPassword>
            </div>

            <div class="flex justify-end">
                <div class="my-3 pr-6">
                    <ConfirmsPassword
                        v-if="!twoFactorEnabled"
                        @confirmed="showTwoFactorAuthentication = true"
                    >
                        <AtomicButton color="yellow" size="sm">
                            Enable 2FA for Sign in
                        </AtomicButton>
                    </ConfirmsPassword>

                    <ConfirmsPassword
                        v-else
                        @confirmed="disableTwoFactorAuthentication"
                    >
                        <AtomicButton
                            color="yellow"
                            size="sm"
                            :class="{ 'opacity-25': loading }"
                            :disabled="loading"
                        >
                            Disable 2FA for Sign in
                        </AtomicButton>
                    </ConfirmsPassword>
                </div>
            </div>
        </div>

        <TwoFactorAuthenticationModal
            v-if="showTwoFactorAuthentication"
            @close="showTwoFactorAuthentication = false"
            @enabled="enabled = true"
        />
        <TwoFactorAuthenticationRecoveryCodesModal
            v-if="showRecoveryCodes"
            @close="showRecoveryCodes = false"
        />
        <TwoFactorAuthenticationPairingCodesModal
            v-if="showPairingCodes"
            @close="showPairingCodes = false"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { usePage } from "@inertiajs/vue3";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";

const globalProps = computed(() => usePage<IRhizomPageProps>().props);
const loading = ref(false);
const showRecoveryCodes = ref(false);
const showTwoFactorAuthentication = ref(false);
const showPairingCodes = ref(false);
const enabled = ref(false);

const twoFactorEnabled = computed(() => {
    return globalProps.value.user.two_factor_enabled || enabled.value;
});

const disableTwoFactorAuthentication = () => {
    loading.value = true;
    router.delete("/user/two-factor-authentication", {
        preserveScroll: true,
        onSuccess: () => {
            loading.value = false;
            enabled.value = false;
        },
    });
};
</script>
